import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { useMoneyFormatter } from "@/lib/currency";
import { MetricCard, PageHeader, Section, Pill } from "@/components/ui-kit";
import DateRange from "@/components/DateRange";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Plus } from "lucide-react";

export default function FinancePage() {
  const [tab, setTab] = useState("overview");

  return (
    <div data-testid="finance-page">
      <PageHeader title="Income & Expenses" subtitle="Operating finance · Posts to journal" />
      <div className="flex gap-2 mb-6 border-b border-border">
        {[
          { k: "overview", label: "Overview" },
          { k: "income-statement", label: "Income Statement" },
          { k: "balance-sheet", label: "Balance Sheet" },
          { k: "transactions", label: "Transactions" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${tab === t.k ? "border-forest text-forest" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`fin-tab-${t.k}`}>{t.label}</button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "income-statement" && <IncomeStatement />}
      {tab === "balance-sheet" && <BalanceSheet />}
      {tab === "transactions" && <Transactions />}
    </div>
  );
}

function buildQuery(params) {
  const qs = Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  return qs ? `?${qs}` : "";
}

function Overview() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const fmt = useMoneyFormatter();

  const reload = useCallback(() => {
    api.get("/finance/summary" + buildQuery({ date_from: range.from, date_to: range.to }))
      .then(({ data }) => setData(data));
  }, [range.from, range.to]);

  useEffect(() => { reload(); }, [reload]);

  if (!data) return <div className="label">Loading…</div>;
  return (
    <>
      <div className="mb-6"><DateRange value={range} onChange={setRange} testId="overview-range" /></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <MetricCard label="Total Income" value={fmt(data.total_income)} icon={TrendingUp} accent="sage" testId="metric-income" />
        <MetricCard label="Total Expense" value={fmt(data.total_expense)} icon={TrendingDown} accent="terracotta" testId="metric-expense" />
        <MetricCard label="Net Income" value={fmt(data.net_income)}
          accent={data.net_income >= 0 ? "forest" : "terracotta"} testId="metric-net" sub={data.net_income >= 0 ? "Profit" : "Loss"} />
      </div>
      <NewTransactionForm onSaved={reload} />
    </>
  );
}

function NewTransactionForm({ onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ type: "INCOME", account_code: "", amount: "", date: new Date().toISOString().slice(0, 10), description: "" });

  useEffect(() => { api.get("/accounting/chart").then(({ data }) => setAccounts(data)); }, []);

  const eligible = accounts.filter((a) => a.type === form.type);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/transactions", {
        ...form,
        amount: Number(form.amount),
        date: form.date ? new Date(form.date + "T12:00:00Z").toISOString() : null,
      });
      toast.success(`${form.type} recorded — journal posted`);
      setForm({ type: form.type, account_code: "", amount: "", date: new Date().toISOString().slice(0, 10), description: "" });
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";
  return (
    <Section title="Record Income / Expense" testId="finance-form">
      <form onSubmit={submit} className="grid md:grid-cols-6 gap-3">
         <input type="date" className={inputCls} value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="fin-date" />
        <select className={inputCls} value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value, account_code: "" })} data-testid="fin-type">
          <option value="INCOME">Income</option>
          <option value="EXPENSE">Expense</option>
        </select>
        <select required className={inputCls} value={form.account_code}
          onChange={(e) => setForm({ ...form, account_code: e.target.value })} data-testid="fin-account">
          <option value="">— Account —</option>
          {eligible.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
        </select>
        <input type="number" required step="0.01" min="0.01" placeholder="Amount" className={inputCls}
          value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="fin-amount" />
        <input required placeholder="Description" className={inputCls}
          value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="fin-description" />
        <button type="submit" className="btn-primary" data-testid="fin-submit-btn"><Plus className="w-4 h-4" /> Record</button>
      </form>
    </Section>
  );
}

function IncomeStatement() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const fmt = useMoneyFormatter();

  useEffect(() => {
    api.get("/accounting/income-statement" + buildQuery({ date_from: range.from, date_to: range.to }))
      .then(({ data }) => setData(data));
  }, [range.from, range.to]);

  if (!data) return <div className="label">Loading…</div>;
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  return (
    <Section title="Income Statement" testId="income-statement"
      actions={<a className="btn-ghost" href={`${BACKEND}/api/reports/export/income-statement?format=pdf`} target="_blank" rel="noreferrer" data-testid="download-pl-pdf">Download PDF</a>}>
      <div className="mb-6"><DateRange value={range} onChange={setRange} testId="is-range" /></div>
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="label mb-3">Income</div>
          {data.income.length === 0 ? <div className="text-sm text-muted-foreground">No income posted in this range.</div> : (
            <table className="w-full text-sm">
              <tbody>
                {data.income.map((r) => (
                  <tr key={r.code} className="border-t border-border">
                    <td className="py-2 font-mono text-xs w-16">{r.code}</td>
                    <td className="py-2">{r.name}</td>
                    <td className="py-2 text-right tabular-nums">{fmt(r.amount)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-foreground font-semibold">
                  <td colSpan={2} className="py-2">Total Income</td>
                  <td className="py-2 text-right tabular-nums">{fmt(data.total_income)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
        <div>
          <div className="label mb-3">Expenses</div>
          {data.expenses.length === 0 ? <div className="text-sm text-muted-foreground">No expenses posted in this range.</div> : (
            <table className="w-full text-sm">
              <tbody>
                {data.expenses.map((r) => (
                  <tr key={r.code} className="border-t border-border">
                    <td className="py-2 font-mono text-xs w-16">{r.code}</td>
                    <td className="py-2">{r.name}</td>
                    <td className="py-2 text-right tabular-nums">{fmt(r.amount)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-foreground font-semibold">
                  <td colSpan={2} className="py-2">Total Expense</td>
                  <td className="py-2 text-right tabular-nums">{fmt(data.total_expense)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="mt-8 pt-6 border-t-2 border-foreground flex items-center justify-between">
        <div className="font-display text-xl font-semibold">Net Income</div>
        <div className={`metric ${data.net_income >= 0 ? "text-forest" : "text-secondary"}`} data-testid="net-income-value">{fmt(data.net_income)}</div>
      </div>
    </Section>
  );
}

function BalanceSheet() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fmt = useMoneyFormatter();

  useEffect(() => {
    if (range.to) {
      setLoading(true);
      api.get("/accounting/balance-sheet" + buildQuery({ as_of: range.to }))
        .then(({ data }) => setData(data))
        .catch((e) => { toast.error("Failed to load balance sheet"); console.error(e); })
        .finally(() => setLoading(false));
    }
  }, [range.to]);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const renderSection = (title, items, total) => (
    <div>
      <div className="label mb-3">{title}</div>
      {items.length === 0 ? <div className="text-sm text-muted-foreground">—</div> : (
        <table className="w-full text-sm">
          <tbody>
            {items.map((r) => (
              <tr key={r.code} className="border-t border-border">
                <td className="py-2 font-mono text-xs w-16">{r.code}</td>
                <td className="py-2">{r.name}</td>
                <td className="py-2 text-right tabular-nums">{fmt(r.amount)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-foreground font-semibold">
              <td colSpan={2} className="py-2">Total {title}</td>
              <td className="py-2 text-right tabular-nums">{fmt(total)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <Section title="Balance Sheet" testId="balance-sheet"
      actions={<a className="btn-ghost" href={`${BACKEND}/api/reports/export/balance-sheet?format=pdf`} target="_blank" rel="noreferrer" data-testid="download-bs-pdf">Download PDF</a>}>
      <div className="mb-6"><DateRange value={range} onChange={setRange} showAsOf testId="bs-range" /></div>
      {loading && <div className="label">Loading…</div>}
      {!loading && data && (
      <div>
      <div className="grid md:grid-cols-3 gap-8">
        {renderSection("Assets", data.assets, data.total_assets)}
        {renderSection("Liabilities", data.liabilities, data.total_liabilities)}
        {renderSection("Equity", data.equity, data.total_equity)}
      </div>
      <div className="mt-8 pt-6 border-t-2 border-foreground flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          A = L + E &rarr; <span className="tabular-nums">{fmt(data.total_assets)}</span> = <span className="tabular-nums">{fmt(data.total_liabilities + data.total_equity)}</span>
        </div>
        <Pill variant={data.balanced ? "success" : "warning"} data-testid="bs-balanced">{data.balanced ? "BALANCED" : "OUT OF BALANCE"}</Pill>
      </div>
      </div>
      )}
      {!loading && !data && <div className="text-sm text-muted-foreground py-6">Select a date to view balance sheet.</div>}
    </Section>
  );
}

function Transactions() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [items, setItems] = useState([]);
  const fmt = useMoneyFormatter();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/finance/transactions" + buildQuery({ date_from: range.from, date_to: range.to }))
      .then(({ data }) => setItems(data));
  }, [range.from, range.to]);

  // Totals across the visible items
  let totalIncome = 0;
  let totalExpense = 0;
  for (const e of items) {
    const accLine = e.lines.find((l) => l.account_code !== "1000") || e.lines[0];
    const amt = (accLine.debit || 0) + (accLine.credit || 0);
    if (e.source_type === "INCOME") totalIncome += amt;
    else if (e.source_type === "EXPENSE") totalExpense += amt;
  }
  const net = totalIncome - totalExpense;

  const exportQs = buildQuery({ format: "pdf", date_from: range.from, date_to: range.to });
  const exportQsCsv = buildQuery({ format: "csv", date_from: range.from, date_to: range.to });

  return (
    <Section title={`Income & Expense entries (${items.length})`} testId="transactions-list"
      actions={
        <div className="flex gap-2">
          <a className="btn-ghost text-xs" target="_blank" rel="noreferrer"
             href={`${BACKEND}/api/reports/export/income-expense-ledger${exportQs}`}
             data-testid="download-ie-pdf">Download PDF</a>
          <a className="btn-ghost text-xs" target="_blank" rel="noreferrer"
             href={`${BACKEND}/api/reports/export/income-expense-ledger${exportQsCsv}`}
             data-testid="download-ie-csv">Download CSV</a>
        </div>
      }>
      <div className="mb-6"><DateRange value={range} onChange={setRange} testId="tx-range" /></div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No income or expense entries in this range.</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left">
            <th className="label py-2">Date</th><th className="label py-2">Type</th><th className="label py-2">Description</th>
            <th className="label py-2">Account</th><th className="label py-2 text-right">Amount</th>
          </tr></thead>
          <tbody>
            {items.map((e) => {
              const accLine = e.lines.find((l) => l.account_code !== "1000") || e.lines[0];
              const amt = (accLine.debit || 0) + (accLine.credit || 0);
              return (
                <tr key={e.id} className="border-t border-border">
                  <td className="py-2.5">{fmtDate(e.date)}</td>
                  <td className="py-2.5"><Pill variant={e.source_type === "INCOME" ? "success" : "warning"}>{e.source_type}</Pill></td>
                  <td className="py-2.5">{e.description}</td>
                  <td className="py-2.5 font-mono text-xs">{accLine.account_code} — {accLine.account_name}</td>
                  <td className="py-2.5 text-right tabular-nums font-medium">{fmt(amt)}</td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-foreground">
              <td colSpan={4} className="py-2.5 text-right label">Total Income</td>
              <td className="py-2.5 text-right tabular-nums font-medium text-forest" data-testid="ie-total-income">{fmt(totalIncome)}</td>
            </tr>
            <tr>
              <td colSpan={4} className="py-2 text-right label">Total Expense</td>
              <td className="py-2 text-right tabular-nums font-medium text-secondary" data-testid="ie-total-expense">{fmt(totalExpense)}</td>
            </tr>
            <tr className="border-t border-border">
              <td colSpan={4} className="py-2.5 text-right label font-semibold">Net Balance</td>
              <td className={`py-2.5 text-right tabular-nums font-semibold ${net >= 0 ? "text-forest" : "text-secondary"}`} data-testid="ie-net-balance">{fmt(net)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </Section>
  );
}